/* eslint-disable max-statements */
import { Alert, Button, Card, FormCheck, Spinner } from 'react-bootstrap';
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

import EqqoVideo from '../../profile/EqqoVideo';
import saveUserProfile from '../../sign-up/saveProfileMutation';
import { useAuth } from '../../services/eqqo.auth.service';
import { useNotifications } from '../../../components/NotificationsContext';
import { useReport } from '../ReportContext';

const instructionVideoID = "zcflsc5n1j";

const Introduction = () => {
	const { user } = useAuth();
	const { reportContent: { acf: { introduction } } } = useReport();
	const { addNotification } = useNotifications();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [loading, setLoading] = useState(false);

	// Track if video is finished, i.e. has been watched
	const [videoFinished, setVideoFinished] = useState(false);
	const [videoWatched, setVideoWatched] = useState(user?.eqqoProfile?.reportInstructionsConfirmed || false);
	const onProgress = useCallback((progress) => {
		if (progress.played >= 0.99) {
			setVideoFinished(true);
		}
	}, []);
	const handleCheckbox = useCallback((elem) => {
		setVideoWatched(elem.target.checked);
	}, []);

	const { mutateAsync } = saveUserProfile(
		useMutation,
		user.databaseId
	);
	const handleViewReportButton = useCallback(async () => {
		setLoading(true);

		if (user?.eqqoProfile?.reportInstructionsConfirmed) {
			navigate("/report/summary");
			setLoading(false);
			return;
		}

		if (videoWatched) {
			try {
				await mutateAsync({
					eqqoProfile: {
						reportInstructionsConfirmed: true
					}
				});
				await Promise.all([
					queryClient.refetchQueries(['auth-user']),
					queryClient.refetchQueries(['respondent', user.respondentId])
				]);
				navigate("/report/summary");
			} catch (error) {
				addNotification({ message: "There was an error saving your confirmation. Please try again." });
			}
		}
		setLoading(false);
	}, [videoWatched]);

	return (
		<div className="report-view">
			<Card body className="bg-white border-0 mb-3">
				<h3>{introduction.video_heading}</h3>

				<EqqoVideo
					className="d-block w-100 mw-100 mb-2"
					config={{
						wistia: {
							options: {
								muted: false,
								playbackRateControl: false,
								playbar: process.env.REACT_APP_SHOW_SCROLL_BAR || false,
								playerColor: "000000",
								plugins: {
									"captions-v1": {
										onByDefault: true
									}
								},
								preload: true,
								videoFoam: true,
								volume: 1
							}
						}
					}}
					onProgress={onProgress}
					style={{ aspectRatio: "16 / 9" }}
					url={`https://fast.wistia.net/embed/iframe/${instructionVideoID}`}
				/>
				<div dangerouslySetInnerHTML={{ __html: introduction?.text }} />

				<Alert className="d-flex" variant="info">
					<FormCheck
						checked={videoWatched}
						disabled={!videoFinished || user?.eqqoProfile?.reportInstructionsConfirmed}
						label={introduction.checkbox_text}
						onChange={handleCheckbox}
						type="checkbox"
					/>
				</Alert>

			</Card>
			<div className="d-flex justify-content-end">
				<Button className="d-flex gap-2" disabled={!videoWatched || loading} onClick={handleViewReportButton}>
					{
						loading
							? <Spinner animation="border" size="sm" />
							: (
								<>
									<div>View Your Report</div>
									<ChevronRightIcon className="i" />
								</>
							)
					}
				</Button>
			</div>
		</div>
	);
}

export default Introduction;
